(() => {
  const mqDesktop = window.matchMedia("(min-width: 901px)");
  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)");

  // --- Tuning knobs ---
  const RISE_PERCENT = 160;     // offscreen travel (% of tile height)
  const STAGGER_MS   = 400;     // per-tile delay
  const EASE         = "cubic-bezier(0.22,1,0.36,1)";
  const TRANSFORM_MS = 1000;
  const OPACITY_MS   = 600;
  const OBS_THRESH   = 0.2;

  // Edge trigger offsets (px)
  const TOP_EXIT_OFFSET    = 0;
  const BOTTOM_EXIT_OFFSET = 0;

  let io = null;
  let lastScrollY = window.scrollY;
  let lastDir = "down";
  let timeouts = [];
  let isVisible = false;
  let hasAnimatedIn = false;   // Avoid double entry when already in view
  let exitDownActive = false;
  let exitUpActive   = false;

  const alternatingOrder = (n) => {
    const out = [];
    let l = 0, r = n - 1;
    while (l <= r) { out.push(l); if (l !== r) out.push(r); l++; r--; }
    return out;
  };

  const setTransition = (el) => {
    el.style.willChange = "transform, opacity";
    el.style.transition = `transform ${TRANSFORM_MS}ms ${EASE}, opacity ${OPACITY_MS}ms ease-out`;
    el.style.transitionDelay = "0ms";
  };

  function clearTimeouts() { for (const t of timeouts) clearTimeout(t); timeouts = []; }

  function clearInlineStyles(wrapper) {
    wrapper.querySelectorAll(".image-grid .image_cont").forEach(el => {
      el.style.transition = "";
      el.style.transitionDelay = "";
      el.style.transform = "";
      el.style.opacity = "";
      el.style.willChange = "";
    });
  }

  function playIn(tiles, fromDir, order) {
    clearTimeouts();
    const fromY = (fromDir === "up") ? -RISE_PERCENT : RISE_PERCENT;

    // If they’re not already offscreen (e.g., after a reversal), push to start
    tiles.forEach(el => {
      setTransition(el);
      // Only set if near parked to avoid flicker mid-animation
      const computed = getComputedStyle(el).transform;
      el.style.transform = `translateY(${fromY}%)`;
      el.style.opacity = "0";
    });

    requestAnimationFrame(() => {
      order.forEach((i, k) => {
        const el = tiles[i];
        timeouts.push(setTimeout(() => {
          el.style.transform = "translateY(0%)";
          el.style.opacity = "1";
        }, k * STAGGER_MS));
      });

      const total = (order.length - 1) * STAGGER_MS + TRANSFORM_MS + 200;
      timeouts.push(setTimeout(() => tiles.forEach(el => el.style.willChange = ""), total));
    });
  }

  function playOut(tiles, toDir, order) {
    clearTimeouts();
    const toY = (toDir === "down") ? -RISE_PERCENT : RISE_PERCENT;

    order.forEach((i, k) => {
      const el = tiles[i];
      setTransition(el);
      timeouts.push(setTimeout(() => {
        el.style.transform = `translateY(${toY}%)`;
        el.style.opacity = "0";
      }, k * STAGGER_MS));
    });

    const total = (order.length - 1) * STAGGER_MS + TRANSFORM_MS + 200;
    timeouts.push(setTimeout(() => tiles.forEach(el => el.style.willChange = ""), total));
  }

  // Reverse a partially/fully started exit back to parked
  function reverseToPark(tiles, /*fromDir*/, order) {
    clearTimeouts();
    const rev = [...order].reverse(); // mirror back in

    rev.forEach((i, k) => {
      const el = tiles[i];
      setTransition(el);
      timeouts.push(setTimeout(() => {
        el.style.transform = "translateY(0%)";
        el.style.opacity = "1";
      }, k * STAGGER_MS));
    });

    const total = (rev.length - 1) * STAGGER_MS + TRANSFORM_MS + 200;
    timeouts.push(setTimeout(() => tiles.forEach(el => el.style.willChange = ""), total));
  }

  function setup() {
    if (!mqDesktop.matches || prefersReduced.matches) { teardown(); return; }

    const wrapper = document.querySelector(".rain-wrapper");
    if (!wrapper) return;

    const tiles = Array.from(wrapper.querySelectorAll(".image-grid .image_cont"));
    if (!tiles.length) return;

    const order = alternatingOrder(tiles.length);

    // --- INITIAL STATE (FOUC-safe): offscreen below + invisible
    tiles.forEach(el => {
      // Set styles immediately so there’s no flash of parked state
      el.style.transition = "none";            // no transition for the initial push
      el.style.transform  = `translateY(${RISE_PERCENT}%)`;
      el.style.opacity    = "0";
    });
    // Force reflow, then enable transitions
    // eslint-disable-next-line no-unused-expressions
    wrapper.offsetHeight;
    tiles.forEach(setTransition);

    // If the wrapper is already in view on load, animate in immediately
    const rect = wrapper.getBoundingClientRect();
    const vh = window.innerHeight;
    const inViewNow = rect.bottom > 0 && rect.top < vh;

    if (inViewNow) {
      hasAnimatedIn = true;
      // Decide direction for entry: if the row starts near the top, "rise from below" feels best
      const dir = (rect.top <= TOP_EXIT_OFFSET + 1) ? "down" : lastDir;
      requestAnimationFrame(() => playIn(tiles, dir, order));
      isVisible = true;
    } else {
      hasAnimatedIn = false;
      isVisible = false;
    }

    // Direction tracking + edge triggers + reversible exits
    const onScroll = () => {
      const y = window.scrollY;
      lastDir = (y > lastScrollY) ? "down" : "up";
      lastScrollY = y;

      if (!isVisible) return;

      const r = wrapper.getBoundingClientRect();
      const vhLocal = window.innerHeight;

      // Exiting upward (scrolling DOWN)
      if (lastDir === "down" && r.top <= TOP_EXIT_OFFSET) {
        if (!exitDownActive) {
          exitDownActive = true; exitUpActive = false;
          playOut(tiles, "down", order);
        }
      }
      // Reverse that exit if user scrolls back UP
      if (exitDownActive && lastDir === "up" && r.top > TOP_EXIT_OFFSET) {
        reverseToPark(tiles, "up", order);
        exitDownActive = false;
      }

      // Exiting downward (scrolling UP)
      if (lastDir === "up" && r.bottom >= (vhLocal - BOTTOM_EXIT_OFFSET)) {
        if (!exitUpActive) {
          exitUpActive = true; exitDownActive = false;
          playOut(tiles, "up", order);
        }
      }
      // Reverse that exit if user scrolls back DOWN
      if (exitUpActive && lastDir === "down" && r.bottom < (vhLocal - BOTTOM_EXIT_OFFSET)) {
        reverseToPark(tiles, "down", order);
        exitUpActive = false;
      }

      // Reset when comfortably inside
      if (r.top > TOP_EXIT_OFFSET && r.bottom < (vhLocal - BOTTOM_EXIT_OFFSET)) {
        exitDownActive = false;
        exitUpActive = false;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    // Intersection observer: if the row enters later (not in view on load), animate in once
    io = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      if (entry.isIntersecting) {
        if (!isVisible) {
          isVisible = true;
          if (!hasAnimatedIn) {
            hasAnimatedIn = true;
            // Choose direction based on how we’re entering now
            const dir = (lastDir === "down") ? "down" : "up";
            playIn(tiles, dir, order);
          }
          exitDownActive = false;
          exitUpActive = false;
        }
      } else {
        if (isVisible) {
          isVisible = false;
          // If it leaves without edge triggers (fast fling), still do a leave
          playOut(tiles, lastDir, order);
          exitDownActive = false;
          exitUpActive = false;
        }
      }
    }, { threshold: OBS_THRESH });

    io.observe(wrapper);
    wrapper._rain_onScroll = onScroll;
  }

  function teardown() {
    if (io) { io.disconnect(); io = null; }
    const wrapper = document.querySelector(".rain-wrapper");
    if (wrapper && wrapper._rain_onScroll) {
      window.removeEventListener("scroll", wrapper._rain_onScroll);
      delete wrapper._rain_onScroll;
    }
    if (wrapper) clearInlineStyles(wrapper);
    clearTimeouts();
    isVisible = false;
    hasAnimatedIn = false;
    exitDownActive = exitUpActive = false;
  }

  mqDesktop.addEventListener("change", () => { teardown(); setup(); });
  prefersReduced.addEventListener("change", () => { teardown(); setup(); });

  setup();
})();
